import { create } from "zustand";
import {
  fetchActiveAccounts,
  fetchAccountPositions,
  fetchPMUsers,
  getBondTypeOptions,
  getSecurityChargeOptions,
  fetchPortfolioRebalance,
  recalculateAccountPosition,
  fetchSnpRatingOptions,
  fetchMoodyRatingOptions,
} from "../http/api";
import { orderBondTypeOptions } from "../Component/OrderEntry/optionsHelper";
import {
  readJob,
  readAllJobs,
  writeJob,
  removeJob,
  JOB_STATUS,
  hasActiveJob,
  createRebalanceRequestId,
  clearRebalanceResultWaiter,
} from "../Utils/portfolioRebalanceJobStorage";
import { cancelPortfolioRebalance } from "../http/api";

export const usePortfolioRebalanceStore = create((set, get) => ({
  // Portfolio summary data
  portfolioSummary: {
    
    rebalance: "",
    currentYield: "",
    // Set default values to "0" for min trade qty, tolerance val, and residual
    minTradeQty: "0",
    toleranceValue: "0",
    residual: "0",
    // New editable fields
    targetValue: "", // float, was targetDuration
    // Added lot size with default 1000
    lotSize: "1000",
  },

  // Simple in-memory cache for the last rebalance request/response
  // Note: cache is embedded on rebalanceApiData._cacheKey (single-entry, in-memory)
  // rebalanceApiData will be cleared on account switch/reset to avoid reuse across accounts
  rebalanceApiData: null,
  rebalanceApiLoading: false,
  rebalanceApiError: null,

  // Async rebalance jobs (ACK + WebSocket/poll), keyed by accountId. In-memory
  // mirror of the localStorage map: { [accountId]: { requestId, status, outcome,
  // seen, accountId } }. Empty when idle. Supports multiple accounts at once.
  rebalanceJobs: {},
  // Non-success result surfaced on the workbench (not-achievable / error / etc.):
  // { title, message } | null
  pendingInfoModal: null,
  // True while the full-screen "Rebalancing..." loader (header's <=60s wait) is
  // showing. Shared so the notification watcher can suppress interaction with
  // its snackbar while the user's own loader is up (see setRebalanceLoaderActive).
  rebalanceLoaderActive: false,

  // Account management
  accounts: [],
  selectedAccount: null,
  pmUsers: [],
  isLoading: false,
  error: null,

  // Account summary data
  accountSummary: {
    totalPar: null,
    averageYieldToWorst: null,
    average: null,
    interest: null,
    averageSPRating: null,
    taxable: null,
    totalCurrValue: null,
    avgYieldToMaturity: null,
    convexity: null,
    next12MonthInterest: null,
    next12MonthPrincipal: null,
    averageMoodys: null,
    nonTaxable: null,
    totalDv01: null,
    accountMstId: null,
    accountIdentifier: null,
    accountDescription: null,
    custodianName: null,
    // pmId: null,
    // pmName: null,
    userName: null,
  },

  // positions data (positions)
  positions: [],
  selectedPositionsData: [], // Array of selected position objects
  selectedBondsTypeCount: {
    total: 0,
    Gov: 0,
    Corp: 0,
    Muni: 0,
  },
  pagination: {
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 20,
  },

  // Add bondType to state
  selectedBondType: "All",
  bondTypeOptions: [],
  bondTypeOptionsLoading: false,
  selectedSecurityCharge: "All",
  securityChargeOptions: [],
  securityChargeOptionsLoading: false,

  // Preview mode
  isPreviewMode: false,
  previewData: null,

  // S&P and Moody's rating options
  snpRatingOptions: [],
  moodyRatingOptions: [],

  // Store all positions (unfiltered) for validation purposes
  allPositionsForValidation: [],

  // Holds the user-added virtual zero-position (not persisted on server)
  virtualZeroPosition: null,

  // When true, fetchAccountPositions auto-selects all bonds for rebalancing.
  // Turned false once the user modifies the selection, so re-fetches preserve their choice.
  selectAllBondsFlag: true,

  // Actions
  setSelectedAccount: (accountId) => {
    // New account = fresh set of positions; allow their stale bonds to recalc.
    resetRecalcTracking();
    set({
      selectedAccount: accountId,
      selectedPositionsData: [],
      selectedBondsTypeCount: { total: 0, Gov: 0, Corp: 0, Muni: 0 },
      // Clear any preview/cache when switching account to avoid accidental reuse
      rebalanceApiData: null,
      isPreviewMode: false,
      // Clear virtual on account change
      virtualZeroPosition: null,
      // New account = auto-select all bonds again
      selectAllBondsFlag: true,
      // Reset form fields when account changes
      portfolioSummary: {
        rebalance: "",
        currentYield: "",
        minTradeQty: "0",
        toleranceValue: "0",
        residual: "0",
        targetValue: "",
        lotSize: "1000", // Reset lot size to default value
      }
    });
    // Positions/validation data are fetched reactively by PortfolioRebalanceScreen's
    // useEffects watching selectedAccount — no direct fetch here to avoid duplicate calls.
  },

  setPreviewMode: (isPreview) => set({ isPreviewMode: isPreview }),

  setPreviewData: (data) => set({ previewData: data }),

  setSelectedBondType: (bondType) => set({ selectedBondType: bondType }),

  setSelectedSecurityCharge: (securityCharge) =>
    set({ selectedSecurityCharge: securityCharge }),

  // Fetch accounts
  fetchAccounts: async (token,userId) => {
    if (!token || !userId) return;

    set({ isLoading: true, error: null });
    try {
      // Pass isPortfolioRebalance=true to sort by accountIdentifier ASC
      const response = await fetchActiveAccounts(
        token,
        0,
        1000,
        userId, // Use userId from auth context
        "accountIdentifier",
        "asc"
      );

      if (!response || !response.content) {
        throw new Error("Invalid response from server");
      }

      const formattedAccounts = response.content.map((account) => ({
        id: account.accountId,
        accountId: account.accountId,
        userId: account.userId || null,
        // pmId: account.pmId,
        // pmName: account.pmName,
        userName: account.userName || null,
        description: account.description,
        custodianName: account.custodianName || "N/A",
        custodianId: account.custodianId,
        accountNumber: account.accountNumber || account.accountIdentifier,
        accountIdentifier: account.accountIdentifier,
        accountType: account.accountType || "Bond",
        status: account.status,
      }));

      // Auto-select first account if none is selected
      const currentSelectedAccount = get().selectedAccount;
      const firstAccount =
        formattedAccounts.length > 0 ? formattedAccounts[0] : null;

      set({
        accounts: formattedAccounts,
        selectedAccount:
          currentSelectedAccount || (firstAccount ? firstAccount.id : null),
        isLoading: false,
      });
      // Positions/validation data are fetched reactively by PortfolioRebalanceScreen's
      // useEffects watching selectedAccount — no direct fetch here to avoid duplicate calls.
    } catch (error) {
      console.error("Error fetching accounts:", error);
      set({
        error: error.message || "Failed to fetch accounts",
        isLoading: false,
      });
    }
  },

  // Fetch PM users
  fetchPMUsers: async (token) => {
    if (!token) return;

    try {
      const data = await fetchPMUsers(token);
      if (!data || !Array.isArray(data)) {
        throw new Error("Invalid PM users response format");
      }
      set({ pmUsers: data });
      return data;
    } catch (error) {
      console.error("Error fetching PM users:", error);
      set({ error: error.message || "Failed to fetch PM users" });
      return [];
    }
  },

  // Fetch bond type options
  fetchBondTypeOptions: async (token) => {
    if (!token) return;

    set({ bondTypeOptionsLoading: true });
    try {
      const bondTypes = await getBondTypeOptions(token);
      const orderedBondTypes = orderBondTypeOptions(bondTypes);
      set({
        bondTypeOptions: orderedBondTypes,
        bondTypeOptionsLoading: false,
      });
      return orderedBondTypes;
    } catch (error) {
      console.error("Error fetching bond types:", error);
      // Fallback to static options if API fails
      const fallbackBondTypes = orderBondTypeOptions(["Gov", "Corp", "Muni"]);
      set({
        bondTypeOptions: fallbackBondTypes,
        bondTypeOptionsLoading: false,
      });
      return fallbackBondTypes;
    }
  },

  // Fetch security charge options
  fetchSecurityChargeOptions: async (token) => {
    if (!token) return;

    set({ securityChargeOptionsLoading: true });
    try {
      const securityCharges = await getSecurityChargeOptions(token);
      set({
        securityChargeOptions: securityCharges || [],
        securityChargeOptionsLoading: false,
      });
      return securityCharges;
    } catch (error) {
      console.error("Error fetching security charges:", error);
      set({
        securityChargeOptions: [],
        securityChargeOptionsLoading: false,
      });
      return [];
    }
  },

  // Fetch S&P rating options
  fetchSnpRatingOptions: async (token) => {
    if (!token) return;
    try {
      const options = await fetchSnpRatingOptions(token);
      set({ snpRatingOptions: options || [] });
      return options;
    } catch (error) {
      set({ snpRatingOptions: [] });
      return [];
    }
  },

  // Fetch Moody's rating options
  fetchMoodyRatingOptions: async (token) => {
    if (!token) return;
    try {
      const options = await fetchMoodyRatingOptions(token);
      set({ moodyRatingOptions: options || [] });
      return options;
    } catch (error) {
      set({ moodyRatingOptions: [] });
      return [];
    }
  },

  // Fetch all positions for validation (without bond type filter)
  fetchAllPositionsForValidation: async (accountId, token) => {
    if (!accountId || !token) return Promise.resolve();

    try {
      // Fetch all positions without any bond type filter
      const response = await fetchAccountPositions(
        token,
        accountId,
        "All", // Always use "All" to get unfiltered positions
        0,
        10000, // Large page size to get all positions
        "updatedAt",
        "desc"
      );

      if (!response || !response.data || !response.data.content) {
        throw new Error("Invalid response from server");
      }

      // Transform the API response to match our table structure (same transformation as fetchAccountPositions)
      const transformedPositions = response.data.content.map((position) => ({
        id: position.accountPositionMappingId,
        accountId: position.accountMstId,
        cusip: position.cusip,
        securityDesc: position.securityDesc,
        description: position.securityDesc,
        quantity: position.quantity,
        securityMarketValue: position.securityMarketValue,
        totalGL:
          (position.securityRealizedGL || 0) +
          (position.securityUnrealizedGL || 0),
        realizedGL: position.securityRealizedGL || 0,
        unrealizedGL: position.securityUnrealizedGL || 0,
        maturityDate: position.maturityDate,
        maturity: position.maturityDate,
        nextCallDate: position.nextCallDate,
        spRating: position.spRating,
        ratingSnp: position.spRating,
        moodyRating: position.moodyRating,
        ratingMoody: position.moodyRating,
        isExemptFromFedTax: position.isExemptFromFedTax,
        yield: position.yield,
        yieldToWorst: position.yieldToWorst,
        yieldValuePp: position.yieldValuePp,
        duration: position.duration,
        modifiedConvexity: position.modifiedConvexity,
        callStartDate: position.callStartDate,
        callEndDate: position.callEndDate,
        dollarConvexity: position.dollarConvexity,
        dv01: position.dv01,
        macaulayDuration: position.macaulayDuration,
        accruedInterest: position.accruedInterest,
        securityCharge: position.securityCharge,
        bondType: position.bondType,
        isCallable: position.isCallable,
        callable: position.isCallable,
        callPrice: position.callPrice,
        price: position.price,
        couponPer: position.couponPer,
        coupon: position.couponPer,
        secMasterId: position.secMasterId,
        bondInventoryId: position.bondInventoryId,
        accountPositionMappingId: position.accountPositionMappingId,
        lastUpdatedBondCal: position.lastUpdatedBondCal,
        conventionMethod: position.conventionMethod,
        incomeSource: position.incomeSource,
        bondSubType: position.bondSubType,
        state: position.state,
        sector: position.sector,
        bondCalcOutputId: position.bondCalcOutputId,
        firstCouponDate: position.firstCouponDate,
        isZeroCoupon: position.isZeroCoupon,
        securityWeightPercentage: position.securityWeightPercentage,
      }));

      // If a virtual zero-position exists, include it for validation purposes
      const v = get().virtualZeroPosition;
      const merged = v ? [v, ...transformedPositions] : transformedPositions;

      set({ allPositionsForValidation: merged });
      return Promise.resolve();
    } catch (error) {
      console.error("Error fetching all positions for validation:", error);
      set({ allPositionsForValidation: [] });
      return Promise.reject(error);
    }
  },

  // Fetch account positions (always fetches 1000 to have all data client-side)
  fetchAccountPositions: async (
    accountId,
    token,
    page = 0,
    size = 1000, // always fetch all
    columnToSort = "updatedAt",
    sortingDirection = "desc",
    bondTypeOverride = undefined, // <-- new argument
    securityChargeOverride = undefined
  ) => {
    if (!accountId || !token) return Promise.resolve();

    set({ isLoading: true, error: null });
    try {
      // Use override if provided, else store value
      const bondType =
        bondTypeOverride !== undefined
          ? bondTypeOverride
          : get().selectedBondType;
      const securityCharge =
        securityChargeOverride !== undefined
          ? securityChargeOverride
          : get().selectedSecurityCharge;
      if(columnToSort === "securityWeightPercentage") {
        columnToSort = "securityMarketValue";
      }
      const response = await fetchAccountPositions(
        token,
        accountId,
        bondType,
        0,    // always page 0
        1000, // always fetch all positions
        columnToSort,
        sortingDirection,
        securityCharge
      );

      if (!response || !response.data || !response.data.content) {
        throw new Error("Invalid response from server");
      }

      // Get currently selected positions (by id)
      const selectedIds = new Set(get().selectedPositionsData.map((p) => p.id));

      const transformedPositions = response.data.content.map((position) => ({
        id: position.accountPositionMappingId,
        accountId: position.accountMstId,
        cusip: position.cusip,
        securityDesc: position.securityDesc, // backend: securityDesc
        description: position.securityDesc, // legacy field for backward compatibility
        quantity: position.quantity,
        securityMarketValue: position.securityMarketValue,
        totalGL:
          (position.securityRealizedGL || 0) +
          (position.securityUnrealizedGL || 0),
        realizedGL: position.securityRealizedGL || 0,
        unrealizedGL: position.securityUnrealizedGL || 0,
        maturityDate: position.maturityDate, // backend: maturityDate
        maturity: position.maturityDate, // legacy field for backward compatibility
        nextCallDate: position.nextCallDate,
        spRating: position.spRating, // backend: spRating
        ratingSnp: position.spRating, // legacy field for backward compatibility
        moodyRating: position.moodyRating, // backend: moodyRating
        ratingMoody: position.moodyRating, // legacy field for backward compatibility
        isExemptFromFedTax: position.isExemptFromFedTax,
        yield: position.yield,
        yieldToWorst: position.yieldToWorst,
        yieldValuePp: position.yieldValuePp,
        duration: position.duration,
        modifiedConvexity: position.modifiedConvexity,
        callStartDate: position.callStartDate,
        callEndDate: position.callEndDate,
        dollarConvexity: position.dollarConvexity,
        dv01: position.dv01,
        macaulayDuration: position.macaulayDuration,
        accruedInterest: position.accruedInterest,
        securityCharge: position.securityCharge,
        bondType: position.bondType,
        isCallable: position.isCallable, // backend: isCallable
        callable: position.isCallable, // legacy field for backward compatibility
        callPrice: position.callPrice,
        price: position.price,
        couponPer: position.couponPer, // backend: couponPer
        coupon: position.couponPer, // legacy field for backward compatibility
        secMasterId: position.secMasterId,
        bondInventoryId: position.bondInventoryId,
        accountPositionMappingId: position.accountPositionMappingId,
        lastUpdatedBondCal: position.lastUpdatedBondCal,
        conventionMethod: position.conventionMethod,
        incomeSource: position.incomeSource,
        bondSubType: position.bondSubType,
        state: position.state,
        sector: position.sector,
        bondCalcOutputId: position.bondCalcOutputId,
        firstCouponDate: position.firstCouponDate,
        isZeroCoupon: position.isZeroCoupon,
        securityWeightPercentage: position.securityWeightPercentage,
        includeInRebalancing: selectedIds.has(
          position.accountPositionMappingId
        ), // <-- retain selection
      }));

      // Helper function to distinguish between zero and undefined/null
      const getValue = (val) => (val === 0 || val === 0.0 || val ? val : null);

      // Extract and map summary data from the API response
      const summary = response.summary || {};
      const accountInfo = response.accountInfo || {};

      const updatedSummary = {
        totalPar: getValue(summary.totalParValue),
        totalCurrValue: getValue(summary.totalCurrentValue),
        averageYieldToWorst: getValue(summary.averageYieldToWorst),
        avgYieldToMaturity: getValue(summary.averageYieldToMaturity),
        average: getValue(summary.averageDuration),
        convexity: getValue(summary.averageConvexity),
        interest: getValue(summary.interest),
        next12MonthInterest: getValue(summary.next12MonthInterest),
        next12MonthPrincipal: getValue(summary.next12MonthPrincipal),
        averageSPRating: summary.averageSPRating || null,
        averageMoodys: summary.averageMoodyRating || null,
        nonTaxable: getValue(summary.totalNonTaxable),
        taxable: getValue(summary.totalTaxable),
        totalDv01: getValue(summary.totalDv01),
        // Add account info
        accountMstId: accountInfo.accountMstId,
        accountIdentifier: accountInfo.accountIdentifier,
        accountDescription: accountInfo.accountDescription,
        custodianName: accountInfo.custodianName,
        // pmId: accountInfo.pmId,
        // pmName: accountInfo.pmName,
        userName: accountInfo.userName || "",
      };

      // If selectAllBondsFlag is true (initial load / account switch), auto-select all.
      // Otherwise preserve the user's existing selection.
      const shouldSelectAll = get().selectAllBondsFlag;

      // Re-include virtual zero position if it exists and is selected
      const existingVirtual = get().virtualZeroPosition;

      if (shouldSelectAll) {
        const allSelected = transformedPositions.map((pos) => ({
          ...pos,
          includeInRebalancing: true,
        }));
        // Include virtual zero position in selected data if it exists
        const allSelectedWithVirtual = existingVirtual
          ? [...allSelected, { ...existingVirtual, includeInRebalancing: true }]
          : allSelected;
        const counts = get().calculateBondTypeCounts(allSelectedWithVirtual);

        set({
          positions: allSelected,
          accountSummary: updatedSummary,
          pagination: {
            totalElements: transformedPositions.length,
            totalPages: 1,
            currentPage: 0,
            pageSize: transformedPositions.length,
          },
          selectedPositionsData: allSelectedWithVirtual,
          selectedBondsTypeCount: counts,
          isLoading: false,
        });
      } else {
        // Preserve user's existing selection
        const selectedIds = new Set(get().selectedPositionsData.map((p) => p.id));
        const updatedPositions = transformedPositions.map((pos) => ({
          ...pos,
          includeInRebalancing: selectedIds.has(pos.id),
        }));
        const newSelected = updatedPositions.filter((p) => p.includeInRebalancing);
        // Re-include virtual zero position if it was selected
        if (existingVirtual && selectedIds.has("virtual-zero-position")) {
          newSelected.push({ ...existingVirtual, includeInRebalancing: true });
        }
        const counts = get().calculateBondTypeCounts(newSelected);

        set({
          positions: updatedPositions,
          accountSummary: updatedSummary,
          pagination: {
            totalElements: transformedPositions.length,
            totalPages: 1,
            currentPage: 0,
            pageSize: transformedPositions.length,
          },
          selectedPositionsData: newSelected,
          selectedBondsTypeCount: counts,
          isLoading: false,
        });
      }
      // Start bond recalculation check asynchronously
      setTimeout(() => {
        checkAndRecalculatePositions(get().positions, token);
      }, 100);
      return Promise.resolve();
    } catch (error) {
      console.error("Error fetching account positions:", error);
      set({
        error: error.message || "Failed to fetch account positions",
        isLoading: false,
        positions: [],
        accountSummary: {
          totalPar: null,
          averageYieldToWorst: null,
          average: null,
          interest: null,
          averageSPRating: null,
          taxable: null,
          totalCurrValue: null,
          avgYieldToMaturity: null,
          convexity: null,
          next12MonthInterest: null,
          next12MonthPrincipal: null,
          averageMoodys: null,
          nonTaxable: null,
          totalDv01: null,
        },
      });
      return Promise.reject(error);
    }
  },

  // Kick off an async rebalance. The endpoint returns an immediate
  // acknowledgment; the final result arrives later via WebSocket or the status
  // poll and is handled by PortfolioRebalanceJobWatcher.
  rebalance: async (requestId) => {
    let { positions, portfolioSummary, virtualZeroPosition } = get();
    // Use all positions (from server) plus optional virtual row
    positions = virtualZeroPosition
      ? [...positions, virtualZeroPosition]
      : positions;
    //add that virtualZeroPosition to positions array if exists
    // Use all positions, but mark includeInRebalancing
    if (!positions || positions.length === 0) {
      set({ rebalanceApiError: "No positions available" });
      return { success: false, message: "No positions available" };
    }
    if (!portfolioSummary.rebalance || !portfolioSummary.targetValue) {
      set({
        rebalanceApiError: "Rebalance type and target value are required",
      });
      return {
        success: false,
        message: "Rebalance type and target value are required",
      };
    }
    // Block a second rebalance for THIS account while it has an active job
    // (pending, or a ready result the user hasn't viewed). Other accounts are
    // unaffected — rebalancing is account-level now.
    const targetAccountId = get().selectedAccount ?? null;
    if (hasActiveJob(targetAccountId)) {
      return {
        success: false,
        alreadyPending: true,
        message:
          "A rebalance is already in progress for this account. Please wait for it to finish.",
      };
    }
    set({ rebalanceApiLoading: true, rebalanceApiError: null });
    try {
      // Map all positions to API format, include the flag
      const bonds = positions.map((position) => ({
        cusip: position.cusip,
        quantity: position.quantity,
        price: position.price,
        securityMarketValue: position.securityMarketValue,
        accruedInterest: position.accruedInterest || 0,
        yield: position.yield,
        duration: position.duration,
        dv01: position.dv01,
        ratingSnp: position.ratingSnp,
        ratingMoody: position.ratingMoody,
        securityMarketValue: position.securityMarketValue,
        includeInRebalancing: !!position.includeInRebalancing,
      }));

      // Determine the current value based on rebalance type
      let currentValue = 0;
      let currentRating = "";
      switch (portfolioSummary.rebalance) {
        case "Duration":
          currentValue = get().accountSummary.average;
          break;
        case "Yield":
          currentValue = get().accountSummary.averageYieldToWorst;
          break;
        case "S&P Rating":
          currentRating = get().accountSummary.averageSPRating;
          break;
        case "Moody's Rating":
          currentRating = get().accountSummary.averageMoodys;
          break;
        case "DV01":
          currentValue =
            get().accountSummary.totalDv01 ;// Use totalDv01 from summary
          break;
        default:
          throw new Error("Invalid rebalance type selected");
      }

      // Map rebalance type to API expected value
      let rebalanceVariable;
      switch (portfolioSummary.rebalance) {
        case "Duration":
          rebalanceVariable = "duration";
          break;
        case "Yield":
          rebalanceVariable = "yield";
          break;
        case "S&P Rating":
          rebalanceVariable = "sprating";
          break;
        case "Moody's Rating":
          rebalanceVariable = "ratingmoody";
          break;
        case "DV01":
          rebalanceVariable = "dv01";
          break;
        default:
          throw new Error("Invalid rebalance type selected");
      }

      // Only parseFloat for numeric types, else pass string
      let targetValue;
      if (
        rebalanceVariable === "duration" ||
        rebalanceVariable === "yield" ||
        rebalanceVariable === "dv01"
      ) {
        targetValue = parseFloat(portfolioSummary.targetValue);
      } else {
        targetValue = portfolioSummary.targetValue;
      }

      // For rating types, send currentRating/targetRating instead of currentValue/targetValue
      let requestBody = {
        rebalanceVariable,
        tolerance: parseFloat(portfolioSummary.toleranceValue),
        tradeMinQty: parseInt(portfolioSummary.minTradeQty),
        // include lot size in payload - fallback to 1000 when empty or invalid
        lotSize: parseInt(portfolioSummary.lotSize || "1000"),
        bonds,
      };
      if (rebalanceVariable === "sprating" || rebalanceVariable === "ratingmoody") {
        requestBody.currentRating = currentRating;
        requestBody.targetRating = targetValue;
      } else {
        requestBody.currentValue = currentValue;
        requestBody.targetValue = targetValue;
      }

      // Cache reuse: if these exact inputs were already computed and their result
      // is still loaded, reuse it instead of starting a new async job (matches the
      // old synchronous behavior: rebalance, go back, rebalance again unchanged →
      // no API call). rebalanceApiData is cleared on account switch / reset, so a
      // stale cross-account result is never reused.
      const cacheKey = buildRebalanceCacheKey(requestBody);
      const existingPreview = get().rebalanceApiData;
      if (existingPreview && existingPreview._cacheKey === cacheKey) {
        set({
          rebalanceApiLoading: false,
          rebalanceApiError: null,
          isPreviewMode: true,
        });
        return { success: true, cached: true };
      }

      // Correlate the async result with this request. The backend echoes back
      // whatever requestId we send (see controller), so we own it end-to-end.
      const activeRequestId = requestId || createRebalanceRequestId();
      requestBody.requestId = activeRequestId;

      console.log("Sending rebalance request:", requestBody);

      // Snapshot everything the Order Preview needs to rebuild the result later
      // (possibly after reload / account switch), independent of live positions.
      const summaryForSnapshot = get().accountSummary;
      const snapshot = {
        positions,
        accountSummary: {
          totalCurrValue: summaryForSnapshot.totalCurrValue,
          average: summaryForSnapshot.average,
          averageYieldToWorst: summaryForSnapshot.averageYieldToWorst,
          totalDv01: summaryForSnapshot.totalDv01,
          averageSPRating: summaryForSnapshot.averageSPRating,
          averageMoodys: summaryForSnapshot.averageMoodys,
          accountIdentifier: summaryForSnapshot.accountIdentifier,
          accountDescription: summaryForSnapshot.accountDescription,
          custodianName: summaryForSnapshot.custodianName,
        },
        portfolioSummary: {
          rebalance: portfolioSummary.rebalance,
          targetValue: portfolioSummary.targetValue,
          toleranceValue: portfolioSummary.toleranceValue,
          minTradeQty: portfolioSummary.minTradeQty,
          lotSize: portfolioSummary.lotSize,
        },
      };

      // Persist the PENDING job (keyed by account) BEFORE the request so a
      // refresh mid-flight can still recover the result via polling.
      writeJob(targetAccountId, {
        requestId: activeRequestId,
        accountId: targetAccountId,
        status: JOB_STATUS.PENDING,
        seen: false,
        createdAt: Date.now(),
        cacheKey, // embedded into the built result so re-submits can reuse it
        snapshot,
        result: null,
      });
      get().syncRebalanceJobs();

      const token = localStorage.getItem("token");
      let ack;
      try {
        ack = await fetchPortfolioRebalance(token, requestBody);
      } catch (error) {
        console.error("Error starting rebalance:", error);
        removeJob(targetAccountId);
        get().syncRebalanceJobs();
        set({
          rebalanceApiError: error.message || "Failed to start rebalance",
          rebalanceApiLoading: false,
        });
        return {
          success: false,
          message: error.message || "Failed to start rebalance",
        };
      }

      // ACK failure (400 / success:false) — abandon the job.
      if (!ack || ack.status === 400 || ack.success === false) {
        removeJob(targetAccountId);
        get().syncRebalanceJobs();
        set({ rebalanceApiLoading: false });
        return {
          success: false,
          status: ack?.status,
          message: ack?.message || "Failed to start rebalance",
        };
      }

      // ACK accepted — the result will be delivered asynchronously (WS/poll).
      set({ rebalanceApiLoading: false });
      return { success: true, ack: true, requestId: activeRequestId, accountId: targetAccountId };
    } catch (error) {
      console.error("Error starting rebalance:", error);
      removeJob(targetAccountId);
      get().syncRebalanceJobs();
      set({
        rebalanceApiError: error.message || "Failed to start rebalance",
        rebalanceApiLoading: false,
      });
      return {
        success: false,
        message: error.message || "Failed to start rebalance",
      };
    }
  },



  // Top-level setters for portfolioSummary fields
  setRebalance: (rebalance) =>
    set((state) => ({
      portfolioSummary: { ...state.portfolioSummary, rebalance },
    })),
  setTargetValue: (targetValue) =>
    set((state) => ({
      portfolioSummary: { ...state.portfolioSummary, targetValue },
    })),
  setMinTradeQty: (minTradeQty) =>
    set((state) => ({
      portfolioSummary: { ...state.portfolioSummary, minTradeQty },
    })),
  setToleranceValue: (toleranceValue) =>
    set((state) => ({
      portfolioSummary: { ...state.portfolioSummary, toleranceValue },
    })),
  setResidual: (residual) =>
    set((state) => ({
      portfolioSummary: { ...state.portfolioSummary, residual },
    })),
  // New setter with validation for lot size (min 1, max 10000000)
  setLotSize: (val) =>
    set((state) => {
      // Allow user to clear the lot size field (empty string)
      if (val === "") {
        return { portfolioSummary: { ...state.portfolioSummary, lotSize: "" } };
      }
      const str = String(val);
      const num = parseInt(str);
      // If valid number within bounds, store as string; otherwise keep previous value
      if (!isNaN(num) && num >= 1 && num <= 10000000) {
        return { portfolioSummary: { ...state.portfolioSummary, lotSize: String(num) } };
      }
      return { portfolioSummary: { ...state.portfolioSummary, lotSize: state.portfolioSummary.lotSize } };
    }),

  // Helper function to calculate bond type counts
  calculateBondTypeCounts: (positions) => {
    const counts = { total: 0, Gov: 0, Corp: 0, Muni: 0 };

    positions.forEach((position) => {
      counts.total++;
      const bondType = position.bondType || "Unknown";
      const bondTypeLower = bondType.toLowerCase();

      // Case insensitive matching
      if (bondTypeLower === "gov" || bondTypeLower === "government") {
        counts.Gov++;
      } else if (bondTypeLower === "corp" || bondTypeLower === "corporate") {
        counts.Corp++;
      } else if (bondTypeLower === "muni" || bondTypeLower === "municipal") {
        counts.Muni++;
      }
    });

    return counts;
  },

  // Selected positions management
  setSelectedPositionsData: (positions) => {
    set((state) => {
      const selectedIds = new Set(positions.map((p) => p.id));
      const updatedPositions = state.positions.map((pos) => ({
        ...pos,
        includeInRebalancing: selectedIds.has(pos.id),
      }));

      // Also update virtual selection if present
      const v = state.virtualZeroPosition
        ? {
            ...state.virtualZeroPosition,
            includeInRebalancing: selectedIds.has("virtual-zero-position"),
          }
        : null;

      const counts = get().calculateBondTypeCounts(positions);
      return {
        positions: updatedPositions,
        virtualZeroPosition: v,
        selectedPositionsData: positions,
        selectedBondsTypeCount: counts,
      };
    });
  },

  togglePositionSelection: (positionId) =>
    set((state) => {
      // User manually changed selection — stop auto-selecting on re-fetch
      const flagUpdate = { selectAllBondsFlag: false };

      // Handle virtual row toggling
      if (positionId === "virtual-zero-position") {
        if (!state.virtualZeroPosition) return state;
        const isSelected = state.selectedPositionsData.some((p) => p.id === "virtual-zero-position");
        let newSelected = isSelected
          ? state.selectedPositionsData.filter((p) => p.id !== "virtual-zero-position")
          : [...state.selectedPositionsData, state.virtualZeroPosition];

        const newCounts = get().calculateBondTypeCounts(newSelected);
        return {
          ...flagUpdate,
          virtualZeroPosition: {
            ...state.virtualZeroPosition,
            includeInRebalancing: !isSelected,
          },
          selectedPositionsData: newSelected,
          selectedBondsTypeCount: newCounts,
        };
      }

      const position = state.positions.find((p) => p.id === positionId);
      if (!position) return state;

      const isSelected = state.selectedPositionsData.some(
        (p) => p.id === positionId
      );
      let newSelected;
      if (isSelected) {
        newSelected = state.selectedPositionsData.filter(
          (p) => p.id !== positionId
        );
      } else {
        newSelected = [...state.selectedPositionsData, position];
      }

      const selectedIds = new Set(newSelected.map((p) => p.id));
      const updatedPositions = state.positions.map((pos) => ({
        ...pos,
        includeInRebalancing: selectedIds.has(pos.id),
      }));
      const newCounts = get().calculateBondTypeCounts(newSelected);

      return {
        ...flagUpdate,
        positions: updatedPositions,
        selectedPositionsData: newSelected,
        selectedBondsTypeCount: newCounts,
      };
    }),

  selectAllPositions: () =>
    set((state) => {
      const allPositions = [
        ...state.positions,
        ...(state.virtualZeroPosition ? [state.virtualZeroPosition] : []),
      ];
      const updatedPositions = state.positions.map((pos) => ({
        ...pos,
        includeInRebalancing: true,
      }));
      return {
        selectAllBondsFlag: true, // all selected again
        positions: updatedPositions,
        virtualZeroPosition: state.virtualZeroPosition
          ? { ...state.virtualZeroPosition, includeInRebalancing: true }
          : null,
        selectedPositionsData: allPositions,
        selectedBondsTypeCount: get().calculateBondTypeCounts(allPositions),
      };
    }),

  clearPositionSelection: () => {
    set((state) => ({
      selectAllBondsFlag: false, // user cleared selection
      positions: state.positions.map((pos) => ({
        ...pos,
        includeInRebalancing: false,
      })),
      virtualZeroPosition: state.virtualZeroPosition
        ? { ...state.virtualZeroPosition, includeInRebalancing: false }
        : null,
      selectedPositionsData: [],
      selectedBondsTypeCount: { total: 0, Gov: 0, Corp: 0, Muni: 0 },
    }));
  },

  // Set/clear virtual zero-position from ZeroPositionEntry
  setVirtualZeroPosition: ({ cusip, price, qty = 0, bondCalcResults, bondDetails }) => {
    // Block if CUSIP exists in holdings (ignore any virtual rows)
    const c = String(cusip || "").trim().toUpperCase();
    const inHoldings =
      (get().allPositionsForValidation || get().positions || []).some(
        (p) => !p?.isVirtualZeroPosition && String(p?.cusip || "").toUpperCase() === c
      );
    if (inHoldings) {
      console.warn("ZeroPositionEntry blocked: CUSIP already exists in holdings:", c);
      return; // do not set
    }

    const accountId = get().selectedAccount || null;

    // Helper to safely read nested props
    const sec = bondDetails?.secMasterDTO || bondDetails || {};
    sec.bondInventoryId = bondDetails.id;
    sec.id = bondDetails.bondDetailsId;
    const ensureNumber = (v) => (v === undefined || v === null || v === "" ? null : Number(v));

    const maturityDate =
      sec.maturityDate || sec.maturity || null;

    const spRating = sec.spRating || sec.ratingSnp || null;
    const moodyRating = sec.moodyRating || sec.ratingMoody || null;

    const desc =
      sec.securityDesc ||
      sec.description ||
      sec.shortDescription ||
      bondDetails?.securityDescription ||
      "Virtual Zero Position";

    const couponPer = sec.couponPer || sec.coupon || sec.couponRate || null;
    const bondType = sec.bondType || "Unknown";
    const securityCharge = sec.securityCharge || bondDetails?.securityCharge || null;
    const isCallable = sec.isCallable ?? sec.callable ?? null;

    const calc = bondCalcResults || {};
    const virtual = {
      // Unique id not clashing with accountPositionMappingId
      id: "virtual-zero-position",
      isVirtualZeroPosition: true,

      // identity
      accountId,
      cusip,
      securityDesc: desc,
      description: desc,

      // quantities/values
      quantity: 0,
      price: ensureNumber(price) ?? ensureNumber(calc.Price),
      accruedInterest: ensureNumber(calc.Accr_Interest) ?? 0,
      securityMarketValue: 0,
      totalGL: 0,
      realizedGL: 0,
      unrealizedGL: 0,

      // dates/ratings
      maturityDate,
      maturity: maturityDate,
      nextCallDate: sec.nextCallDate || null,
      callStartDate: sec.callStartDate || null,
      callEndDate: sec.callEndDate || null,

      spRating,
      ratingSnp: spRating,
      moodyRating,
      ratingMoody: moodyRating,

      // calcs
      yield: ensureNumber(calc.Yield),
      yieldToWorst: ensureNumber(calc.YieldToWorst),
      yieldValuePp: ensureNumber(calc.YieldValuePP),
      duration: ensureNumber(calc.Duration),
      modifiedConvexity: ensureNumber(calc.Modified_Convexity),
      dollarConvexity: ensureNumber(calc.Dollar_Convexity),
      dv01: ensureNumber(calc.DV01),
      macaulayDuration: ensureNumber(calc.Macaulay_Duration),

      // descriptors
      bondType,
      securityCharge,
      isCallable,
      callPrice: sec.callPrice || null,
      couponPer,
      coupon: couponPer,
      
      // Add bondInventoryId and secMasterId from bond details
      bondInventoryId: sec.bondInventoryId || bondDetails?.bondInventoryId || null,
      secMasterId: sec.secMasterId || bondDetails?.secMasterId || null,
      
      // flags
      includeInRebalancing: true,
    };

    set((state) => {
      // Add to selected rows if checked
      const newSelected = [...state.selectedPositionsData.filter(p => p.id !== "virtual-zero-position")];
      if (virtual.includeInRebalancing) newSelected.push(virtual);
      const newCounts = get().calculateBondTypeCounts(newSelected);

      return {
        virtualZeroPosition: virtual,
        selectedPositionsData: newSelected,
        selectedBondsTypeCount: newCounts,
      };
    });
  },

  clearVirtualZeroPosition: () => {
    set((state) => {
      const filteredSelected = state.selectedPositionsData.filter(p => p.id !== "virtual-zero-position");
      const newCounts = get().calculateBondTypeCounts(filteredSelected);
      return {
        virtualZeroPosition: null,
        selectedPositionsData: filteredSelected,
        selectedBondsTypeCount: newCounts,
      };
    });
  },

  // Reset function
  reset: () =>
    set({
      portfolioSummary: {
        totalParValue: 0,
        totalCurrentValue: 0,
        avgYieldToWorst: 0,
        avgYieldToMaturity: 0,
        avgDuration: 0,
        avgSPRating: "",
        avgMoodysRating: "",
        totalNonTaxable: 0,
        convexity: 0,
        totalTaxable: 0,
        rebalance: "",
        currentYield: "",
        minTradeQty: "0",
        toleranceValue: "0",
        residual: "0",
        targetValue: "",
        lotSize: "1000",
      },
      positions: [],
      selectedAccount: null,
      accounts: [],
      pmUsers: [],
      selectedPositionsData: [],
      selectedBondsTypeCount: {
        total: 0,
        Gov: 0,
        Corp: 0,
        Muni: 0,
      },
      selectedBondType: "All",
      selectedSecurityCharge: "All",
      // Clear preview/cache and virtual on reset
      rebalanceApiData: null,
      virtualZeroPosition: null,
      selectAllBondsFlag: true,
    }),

  // WebSocket update handlers
  updateAccountInList: (updatedAccount) => {
    const accountIdentifier =
      updatedAccount.accountId || updatedAccount.accountMstId;

    set((state) => ({
      accounts: state.accounts.map((acc) =>
        acc.id === accountIdentifier ? { ...acc, ...updatedAccount } : acc
      ),
    }));
  },

  removeAccountFromList: (accountId) => {
    set((state) => ({
      accounts: state.accounts.filter((acc) => acc.id !== accountId),
    }));
  },

  updatePositionInList: (updatedPosition) => {
    const transformedPosition = {
      id: updatedPosition.accountPositionMappingId,
      accountId: updatedPosition.accountMstId,
      cusip: updatedPosition.cusip,
      securityDesc: updatedPosition.securityDesc, // backend: securityDesc
      description: updatedPosition.securityDesc, // legacy field for backward compatibility
      quantity: updatedPosition.quantity,
      securityMarketValue: updatedPosition.securityMarketValue,
      totalGL:
        (updatedPosition.securityRealizedGL || 0) +
        (updatedPosition.securityUnrealizedGL || 0),
      realizedGL: updatedPosition.securityRealizedGL || 0,
      unrealizedGL: updatedPosition.securityUnrealizedGL || 0,
      maturityDate: updatedPosition.maturityDate, // backend: maturityDate
      maturity: updatedPosition.maturityDate, // legacy field for backward compatibility
      nextCallDate: updatedPosition.nextCallDate,
      spRating: updatedPosition.spRating, // backend: spRating
      ratingSnp: updatedPosition.spRating, // legacy field for backward compatibility
      moodyRating: updatedPosition.moodyRating, // backend: moodyRating
      ratingMoody: updatedPosition.moodyRating, // legacy field for backward compatibility
      isExemptFromFedTax: updatedPosition.isExemptFromFedTax,
      yield: updatedPosition.yield,
      yieldToWorst: updatedPosition.yieldToWorst,
      yieldValuePp: updatedPosition.yieldValuePp,
      duration: updatedPosition.duration,
      modifiedConvexity: updatedPosition.modifiedConvexity,
      callStartDate: updatedPosition.callStartDate,
      callEndDate: updatedPosition.callEndDate,
      dollarConvexity: updatedPosition.dollarConvexity,
      dv01: updatedPosition.dv01,
      macaulayDuration: updatedPosition.macaulayDuration,
      accruedInterest: updatedPosition.accruedInterest,
      securityCharge: updatedPosition.securityCharge,
      bondType: updatedPosition.bondType,
      isCallable: updatedPosition.isCallable, // backend: isCallable
      callable: updatedPosition.isCallable, // legacy field for backward compatibility
      callPrice: updatedPosition.callPrice,
      price: updatedPosition.price,
      couponPer: updatedPosition.couponPer, // backend: couponPer
      coupon: updatedPosition.couponPer, // legacy field for backward compatibility
      secMasterId: updatedPosition.secMasterId,
      bondInventoryId: updatedPosition.bondInventoryId,
      accountPositionMappingId: updatedPosition.accountPositionMappingId,
      lastUpdatedBondCal: updatedPosition.lastUpdatedBondCal,
      conventionMethod: updatedPosition.conventionMethod,
      incomeSource: updatedPosition.incomeSource,
      bondSubType: updatedPosition.bondSubType,
      state: updatedPosition.state,
      sector: updatedPosition.sector,
      bondCalcOutputId: updatedPosition.bondCalcOutputId,
      firstCouponDate: updatedPosition.firstCouponDate,
      isZeroCoupon: updatedPosition.isZeroCoupon,
      securityWeightPercentage: updatedPosition.securityWeightPercentage,
    };

    set((state) => ({
      positions: state.positions.map((pos) =>
        pos.accountPositionMappingId ===
        transformedPosition.accountPositionMappingId
          ? { ...pos, ...updatedPosition }
          : pos
      ),
      selectedPositionsData: state.selectedPositionsData.map((pos) =>
        pos.accountPositionMappingId ===
        transformedPosition.accountPositionMappingId
          ? { ...pos, ...updatedPosition }
          : pos
      ),
    }));
  },

  updateAccountSummary: (summaryUpdate) => {
    const mappedSummary = {
      totalPar: summaryUpdate.totalParValue ?? null,
      totalCurrValue: summaryUpdate.totalCurrentValue ?? null,
      averageYieldToWorst: summaryUpdate.averageYieldToWorst ?? null,
      avgYieldToMaturity: summaryUpdate.averageYieldToMaturity ?? null,
      average: summaryUpdate.averageDuration ?? null,
      convexity: summaryUpdate.averageConvexity ?? null,
      interest: summaryUpdate.interest ?? null,
      next12MonthPrincipal: summaryUpdate.next12MonthPrincipal ?? null,
      next12MonthInterest: summaryUpdate.next12MonthInterest ?? null,
      averageSPRating: summaryUpdate.averageSPRating ?? null,
      averageMoodys: summaryUpdate.averageMoodyRating ?? null,
      nonTaxable: summaryUpdate.totalNonTaxable ?? null,
      totalDv01: summaryUpdate.totalDv01 ?? null,
      taxable: summaryUpdate.totalTaxable ?? null,
      accountMstId: summaryUpdate.accountMstId,
      accountIdentifier: summaryUpdate.accountIdentifier,
      accountDescription: summaryUpdate.accountDescription,
      custodianName: summaryUpdate.custodianName,
      // pmId: summaryUpdate.pmId,
      userName: summaryUpdate.userName,
      userId: summaryUpdate.userId,
    };

    set((state) => ({
      accountSummary: {
        ...state.accountSummary,
        ...mappedSummary,
      },
    }));
  },

  clearAchievedTargets: () => {
    set((state) => ({
      rebalanceApiData: state.rebalanceApiData
        ? {
            ...state.rebalanceApiData,
            targetValueAchieved: null,
            targetRatingAchieved: null,
          }
        : null,
    }));
  },

  // ---- Async rebalance jobs (ACK + WebSocket/poll), keyed by accountId ----

  // Refresh the in-memory job mirror from the localStorage map (the single
  // source of truth). Called after any read/write. Also initialises on app load.
  syncRebalanceJobs: () => {
    const jobs = readAllJobs();
    const mirror = {};
    Object.keys(jobs).forEach((accountId) => {
      const job = jobs[accountId];
      mirror[accountId] = {
        requestId: job.requestId,
        status: job.status,
        outcome: job.result?.outcome ?? null,
        seen: !!job.seen,
        accountId: job.accountId ?? accountId,
      };
    });
    set({ rebalanceJobs: mirror });
  },

  // Remove one account's job (after it is viewed / consumed). Also fire a
  // best-effort backend delete so the server-side result entry is cleaned up
  // once the user has viewed it (same DELETE endpoint the cancel path uses).
  // The delete is non-fatal: the local removal always happens regardless of the
  // network result, matching cancelRebalance's fire-and-forget behavior.
  clearRebalanceJob: (accountId) => {
    const job = readJob(accountId);
    if (job?.requestId) {
      const token = localStorage.getItem("token");
      cancelPortfolioRebalance(token, job.requestId).catch((e) =>
        console.warn("[rebalance-job] delete request failed", e)
      );
    }
    removeJob(accountId);
    get().syncRebalanceJobs();
  },

  // Cancel an in-flight rebalance for an account: stop tracking it locally and
  // attempt to notify the backend using the requestId. If a late result still
  // arrives, it is ignored because the local job no longer matches.
  cancelRebalance: (accountId) => {
    const job = readJob(accountId);
    if (job?.requestId) {
      clearRebalanceResultWaiter(job.requestId); // stop any active 60s waiter
      const token = localStorage.getItem("token");
      // Fire-and-forget; the backend delete endpoint is invoked with the job
      // requestId and any failure is handled non-fatally.
      cancelPortfolioRebalance(token, job.requestId).catch((e) =>
        console.warn("[rebalance-job] cancel request failed", e)
      );
    }
    removeJob(accountId);
    get().syncRebalanceJobs();
  },

  // Load a READY result's prebuilt preview data into the store; callers then
  // navigate to the Order Preview screen (data is present before nav, so the
  // screen's "no data" redirect guard does not bounce).
  loadReadyResultIntoPreview: (rebalanceApiData) =>
    set({
      rebalanceApiData,
      isPreviewMode: true,
      rebalanceApiLoading: false,
      rebalanceApiError: null,
    }),

  // Restore the request-time context captured in the job snapshot so the Order
  // Preview renders and submits against the correct account even after a reload
  // or account switch clears the live state. Uses a plain set for selectedAccount
  // to avoid wiping rebalanceApiData, and merges summaries instead of replacing them.
  restoreRebalanceContext: ({ accountId, portfolioSummary, accountSummary } = {}) =>
    set((state) => ({
      selectedAccount: accountId != null ? accountId : state.selectedAccount,
      portfolioSummary: portfolioSummary
        ? { ...state.portfolioSummary, ...portfolioSummary }
        : state.portfolioSummary,
      accountSummary: accountSummary
        ? { ...state.accountSummary, ...accountSummary }
        : state.accountSummary,
    })),

  // Non-success result to surface on the workbench: { title, message } | null
  setPendingInfoModal: (payload) => set({ pendingInfoModal: payload }),
  clearPendingInfoModal: () => set({ pendingInfoModal: null }),

  // Toggled by the header around its <=60s "Rebalancing..." loader.
  setRebalanceLoaderActive: (active) => set({ rebalanceLoaderActive: active }),
}));

/**
 * Build the Order Preview data object from a raw RebalanceResponseDTO plus the
 * request-time snapshot (positions + accountSummary). Extracted from the old
 * synchronous rebalance() so it can be reused when the async result arrives via
 * WebSocket or poll. The shape is identical to what OrderPreviewScreen expects
 * on rebalanceApiData (tradesWithDetails, originalPlusUpdatedTrades, summary).
 */
export function buildRebalanceApiData(
  responseData,
  { positions = [], accountSummary = {} } = {}
) {
  const tradeCusips = new Set((responseData.trades || []).map((t) => t.cusip));
  const tradesWithDetails = (responseData.trades || []).map((trade) => {
    const position = positions.find((p) => p.cusip === trade.cusip);
    return { ...position, ...trade };
  });

  // Positions not present in trades are shown un-included.
  const untouchedPositions = positions
    .filter((p) => !tradeCusips.has(p.cusip))
    .map((p) => ({ ...p, includeInRebalancing: false }));

  const originalPlusUpdatedTrades = [...tradesWithDetails, ...untouchedPositions];

  const buyTrades = tradesWithDetails.filter(
    (t) => String(t.side).toUpperCase() === "BUY"
  );
  const sellTrades = tradesWithDetails.filter(
    (t) => String(t.side).toUpperCase() === "SELL"
  );
  const buyTradeValue = buyTrades.length;
  const sellTradeValue = sellTrades.length;
  const netTradeValue = tradesWithDetails.reduce(
    (sum, t) => sum + (t.tradeValue || 0),
    0
  );
  const buyTradeAmt = buyTrades.reduce((sum, t) => sum + (t.tradeValue || 0), 0);
  const sellTradeAmt = sellTrades.reduce((sum, t) => sum + (t.tradeValue || 0), 0);

  const totalCurrValue = accountSummary.totalCurrValue || 0;
  const turnover = totalCurrValue > 0 ? netTradeValue / totalCurrValue : 0;

  return {
    ...responseData,
    tradesWithDetails,
    originalPlusUpdatedTrades,
    summary: {
      buyTradeValue,
      buyTradeAmt,
      sellTradeValue,
      sellTradeAmt,
      netTradeValue,
      turnover,
      totalEndingMarketValue: responseData.totalEndingMarketValue,
    },
    beforeEditedAchievedTargetValue: responseData.targetValueAchieved,
    beforeEditedAchievedTargetRating: responseData.targetRatingAchieved,
  };
}
/**
 * Helper function to check if a position's bond calculation needs updating
 * @param {string} lastUpdatedBondCalDate - UTC timestamp of last calculation
 * @returns {boolean} - Whether recalculation is needed
 */
function needsRecalculation(lastUpdatedBondCalDate) {
  if (!lastUpdatedBondCalDate) return false;

  // Ensure the timestamp is treated as UTC by appending 'Z' if not present
  const timestamp = lastUpdatedBondCalDate.endsWith("Z")
    ? lastUpdatedBondCalDate
    : lastUpdatedBondCalDate + "Z";

  // Parse as UTC
  const lastUpdated = new Date(timestamp);

  // Handle invalid date
  if (isNaN(lastUpdated)) return false;

  // Get current UTC date
  const now = new Date();

  // Compare UTC year, month, and day
  return (
    lastUpdated.getUTCFullYear() !== now.getUTCFullYear() ||
    lastUpdated.getUTCMonth() !== now.getUTCMonth() ||
    lastUpdated.getUTCDate() !== now.getUTCDate()
  );
}
// --- Recalculation loop guards ---------------------------------------------
// A recalc PUT mutates the position on the backend, which broadcasts an
// accountSummary/positionUpdate WS event, which used to re-fetch positions and
// re-run this sweep -> a feedback loop that re-recalculated the same positions
// endlessly (the "sooooo many requests" storm). These two guards break it:
//   - recalcInFlight: no overlapping sweeps run at once.
//   - recalcRequestedIds: a position is recalc'd AT MOST ONCE per account per
//     session. Asking the backend to recompute today's bond calc twice does
//     nothing new, so skipping repeats is both correct and loop-proof even if
//     fetchAccountPositions fires many times.
let recalcInFlight = false;
let recalcRequestedIds = new Set();

/**
 * Clears the per-session recalc dedup tracking. Call when the active account
 * changes so a freshly selected account can recalc its own stale positions.
 */
function resetRecalcTracking() {
  recalcInFlight = false;
  recalcRequestedIds = new Set();
}

/**
 * Checks all positions and triggers recalculations where needed
 * @param {Array} positions - List of positions to check
 * @param {string} token - Authentication token
 */
async function checkAndRecalculatePositions(positions, token) {
  if (!positions || !positions.length || !token) return;

  // Don't start a new sweep while one is still running.
  if (recalcInFlight) {
    console.log("Bond recalculation already in progress, skipping sweep.");
    return;
  }

  console.log("Checking positions for bond recalculation needs...");

  const positionsNeedingUpdate = positions.filter(
    (position) =>
      position.lastUpdatedBondCal != null &&
      position.accountPositionMappingId &&
      // Skip positions we've already asked the backend to recalc this session.
      !recalcRequestedIds.has(position.accountPositionMappingId) &&
      needsRecalculation(position.lastUpdatedBondCal)
  );

  if (positionsNeedingUpdate.length === 0) {
    console.log("All positions are up to date with bond calculations.");
    return;
  }

  console.log(
    `Found ${positionsNeedingUpdate.length} positions needing bond recalculation.`
  );

  recalcInFlight = true;
  try {
    // Process each position that needs recalculation
    for (const position of positionsNeedingUpdate) {
      // Mark as requested up front so a concurrent refetch can't queue it again.
      recalcRequestedIds.add(position.accountPositionMappingId);
      try {
        console.log(
          `Recalculating bond for position ${position.cusip} (ID: ${position.accountPositionMappingId})`
        );
        await recalculateAccountPosition(
          token,
          position.accountPositionMappingId
        );
      } catch (error) {
        console.error(
          `Error recalculating position ${position.accountPositionMappingId}:`,
          error
        );
        // Continue with other positions even if one fails
      }
    }
  } finally {
    recalcInFlight = false;
  }

  console.log("Bond recalculation check completed.");
}

/**
 * Build a stable cache key for a rebalance request body. Sorts bonds by CUSIP and
 * normalizes numeric fields before hashing, so logically identical payloads
 * produce the same key even if order differs. Excludes requestId (which changes
 * on every call) so re-submitting unchanged inputs reuses the prior result.
 */
function buildRebalanceCacheKey(requestBody) {
  const clone = JSON.parse(JSON.stringify(requestBody || {}));
  delete clone.requestId; // per-request id must not affect the key
  if (Array.isArray(clone.bonds)) {
    clone.bonds = clone.bonds
      .map((b) => ({
        cusip: b.cusip,
        quantity: toNum(b.quantity),
        price: toNum(b.price),
        accruedInterest: toNum(b.accruedInterest),
        yield: toNum(b.yield),
        duration: toNum(b.duration),
        dv01: toNum(b.dv01),
        ratingSnp: b.ratingSnp || null,
        ratingMoody: b.ratingMoody || null,
        includeInRebalancing: !!b.includeInRebalancing,
      }))
      .sort((a, b) => (a.cusip || "").localeCompare(b.cusip || ""));
  }
  if (Object.prototype.hasOwnProperty.call(clone, "tolerance")) {
    clone.tolerance = toNum(clone.tolerance);
  }
  if (Object.prototype.hasOwnProperty.call(clone, "tradeMinQty")) {
    clone.tradeMinQty = toNum(clone.tradeMinQty);
  }
  if (Object.prototype.hasOwnProperty.call(clone, "currentValue")) {
    clone.currentValue = toNum(clone.currentValue);
  }
  // For ratings, targetRating remains string; for numeric targets, normalize
  if (Object.prototype.hasOwnProperty.call(clone, "targetValue")) {
    clone.targetValue = toNum(clone.targetValue);
  }
  const stable = stableStringifyForCache(clone);
  return fnv1aHash(stable);
}

function toNum(v) {
  const n = Number(v);
  if (isNaN(n)) return null;
  // Quantize to fixed precision to avoid tiny float diffs (e.g., 0.30000000004 vs 0.3)
  const decimals = 8;
  return Number.isFinite(n) ? Number(n.toFixed(decimals)) : n;
}

function stableStringifyForCache(obj) {
  if (Array.isArray(obj)) {
    return '[' + obj.map(stableStringifyForCache).join(',') + ']';
  }
  if (obj && typeof obj === 'object') {
    const keys = Object.keys(obj).sort();
    return '{' + keys.map((k) => JSON.stringify(k) + ':' + stableStringifyForCache(obj[k])).join(',') + '}';
  }
  return JSON.stringify(obj);
}

// FNV-1a 32-bit hash, returns hex string
function fnv1aHash(str) {
  let h = 0x811c9dc5; // 2166136261
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16);
}
